// Explorer Discovery Service - Organizational risk discovery for Explorer assessments
// Uses Explorer-specific endpoints and Redux state (explorerRisks, not modelRisks)
import {
  addExplorerRisksBatch,
  updateExplorerAreaStatus,
  setExplorerAreaAnalysis,
  EXPLORER_RISK_AREAS
} from '../../redux/slices/risksSlice';
import { v4 as uuid } from 'uuid';
import { authFetch } from '../../utils/authFetch';

export class ExplorerDiscoveryService {
  constructor(dispatch, modelId, assessmentUuid, parentAssessmentUuid, businessContext, user, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.parentAssessmentUuid = parentAssessmentUuid; // For inherited Builder risks context
    this.businessContext = businessContext;
    this.user = user; // Current authenticated user
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeDiscovery(areaKey, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg, inheritedRisks, progressCallback) {
    try {
      // Update area status to in_progress
      this.dispatch(updateExplorerAreaStatus({
        assessmentUuid: this.assessmentUuid,
        areaKey,
        status: 'in_progress'
      }));

      // Task 1: Analyzing Risk Area (Explorer-specific)
      if (progressCallback) progressCallback('analyzing risk area');
      await new Promise(resolve => setTimeout(resolve, 500));

      const areaRes = await authFetch('/.netlify/functions/explorerAnalyzeRiskArea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId: this.modelId,
          assessmentUuid: this.assessmentUuid,
          parentAssessmentUuid: this.parentAssessmentUuid,
          areaKey,
          areaDefinition,
          contextSummaries: useCaseAnalysisData,
          businessContext: this.businessContext,
          inheritedRisks: inheritedRisks // Builder risks for context
        })
      });

      if (!areaRes.ok) throw new Error(await areaRes.text().catch(() => 'Area analysis failed'));
      const { areaCard } = await areaRes.json();

      // Store area analysis in Redux
      this.dispatch(setExplorerAreaAnalysis({
        assessmentUuid: this.assessmentUuid,
        areaKey,
        analysis: {
          summary: areaCard?.summary || [],
          analysis: areaCard?.analysis || ''
        }
      }));

      // LIVE UPDATE: Area analysis complete
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('area', { areaCard, areaKey });
      }

      // Task 2: Researching Risk Sources (Explorer-specific)
      if (progressCallback) progressCallback('researching risk sources');
      await new Promise(resolve => setTimeout(resolve, 800));

      const sourcesRes = await authFetch('/.netlify/functions/explorerResearchRiskSources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId: this.modelId,
          assessmentUuid: this.assessmentUuid,
          parentAssessmentUuid: this.parentAssessmentUuid,
          areaKey,
          areaDefinition,
          contextSummaries: useCaseAnalysisData,
          businessContext: this.businessContext,
          inheritedRisks: inheritedRisks
        })
      });

      if (!sourcesRes.ok) throw new Error(await sourcesRes.text().catch(() => 'Source research failed'));
      const { selectedSources } = await sourcesRes.json();

      // LIVE UPDATE: Source research complete
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('sources', { selectedSources, areaKey });
      }

      // Task 3: Researching Root Causes
      if (progressCallback) progressCallback('researching root causes');
      await new Promise(resolve => setTimeout(resolve, 800));

      let causesBySource = {};
      try {
        const causesRes = await authFetch('/.netlify/functions/explorerResearchRootCauses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            modelId: this.modelId,
            assessmentUuid: this.assessmentUuid,
            parentAssessmentUuid: this.parentAssessmentUuid,
            areaKey,
            areaDefinition,
            selectedSources,
            contextSummaries: useCaseAnalysisData,
            businessContext: this.businessContext,
            evaluationCriteria: riskCauseEvalCfg,
            inheritedRisks: inheritedRisks
          })
        });

        if (causesRes.ok) {
          const data = await causesRes.json();
          causesBySource = data.causesBySource || {};
        } else {
          console.warn('[ExplorerDiscoveryService] Root cause research failed, using fallback approach');
          causesBySource = this.generateFallbackCauses(selectedSources, areaDefinition);
        }
      } catch (causesError) {
        console.warn('[ExplorerDiscoveryService] Root cause research timed out, using fallback approach:', causesError.message);
        causesBySource = this.generateFallbackCauses(selectedSources, areaDefinition);
      }

      // LIVE UPDATE: Cause research complete
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('causes', { causesBySource, areaKey });
      }

      // Task 4: Extract Relevant Causes for RAG
      if (progressCallback) progressCallback('preparing causes for RAG');
      await new Promise(resolve => setTimeout(resolve, 200));

      const relevantCausesForRAG = this.extractRelevantCausesForRAG(selectedSources, causesBySource, useCaseAnalysisData);

      console.log(`[ExplorerDiscoveryService] Extracted ${relevantCausesForRAG.length} relevant causes for RAG enhancement`);

      // LIVE UPDATE: Cause preparation complete
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('causesForRAG', {
          relevantCauses: relevantCausesForRAG,
          totalCauseCategories: Object.keys(causesBySource).length,
          areaKey
        });
      }

      // Task 5: RAG-Powered Scenarios (Explorer-specific)
      if (progressCallback) progressCallback('scenarios');
      await new Promise(resolve => setTimeout(resolve, 1500));

      let scenarios = [];
      let ragMetadata = {};
      try {
        console.log(`[ExplorerDiscoveryService] SENDING TO RAG: ${relevantCausesForRAG.length} causes`);

        const scenRes = await authFetch('/.netlify/functions/explorerGenerateScenariosWithRAG', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            card: areaCard,
            modelNarratives: useCaseAnalysisData,
            areaKey,
            areaDefinition,
            selectedCatalogues: [],
            useAllCatalogues: true,
            selectedSources: selectedSources,
            relevantCauses: relevantCausesForRAG,
            contextEnrichment: true,
            businessContext: this.businessContext,
            inheritedRisks: inheritedRisks,
            assessmentUuid: this.assessmentUuid,         // backend looks up applied_register_ids
            parentAssessmentUuid: this.parentAssessmentUuid
          })
        });

        if (scenRes.ok) {
          const scenData = await scenRes.json();
          scenarios = scenData.scenarios || [];

          ragMetadata = {
            method: scenData.method || 'RAG',
            cataloguesUsed: scenData.cataloguesUsed || [],
            retrievedRisks: scenData.retrievedRisks || [],
            generationSource: 'Explorer-RAG-Enhanced'
          };

          // Auto-promote scenarios to Explorer risks
          console.log(`[ExplorerDiscoveryService] Auto-promoting ${scenarios.length} scenarios to Explorer risks`);
          const promotionStartTime = Date.now();

          const risksToAdd = scenarios.map((scenario, index) => {
            const riskId = uuid();
            const userSourceName = selectedSources?.[index % selectedSources.length]?.sourceName || 'Generated Risk Source';

            // IMPORTANT: addExplorerRisksBatch expects { category, risk: {...} } format
            return {
              category: userSourceName,
              risk: {
                riskId,
                riskName: scenario.scenarioText || scenario.title || 'Generated Risk Scenario',
                rationale: scenario.rationale || scenario.evidence || '',
                status: 'Saved',
                generationMethod: ragMetadata.method,
                cataloguesUsed: ragMetadata.cataloguesUsed,
                entity: scenario.entity,
                intent: scenario.intent,
                timing: scenario.timing,
                other: scenario.other,
                confidence: scenario.confidence,
                areaKey,
                type: 'Explorer'
              }
            };
          });

          // Batch add risks to Redux
          if (risksToAdd.length > 0) {
            this.dispatch(addExplorerRisksBatch({
              assessmentUuid: this.assessmentUuid,
              areaKey,
              risks: risksToAdd
            }));

            // Persist risks to database
            await this.saveRisksToDatabase(risksToAdd, areaKey);
          }

          const promotionDuration = Date.now() - promotionStartTime;
          console.log(`[ExplorerDiscoveryService] All ${risksToAdd.length} scenarios promoted in ${promotionDuration}ms`);

          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('scenarios', {
              scenarios,
              ragMetadata: {
                ...ragMetadata,
                inputCauses: relevantCausesForRAG.length,
                catalogueCount: ragMetadata.cataloguesUsed?.length || 0,
                catalogueNames: ragMetadata.cataloguesUsed || [],
                retrievedRisksCount: ragMetadata.retrievedRisks?.length || 0,
                generationSummary: `Generated ${scenarios.length} organizational risk entries from ${relevantCausesForRAG.length} causes`
              },
              totalGenerated: scenarios.length,
              totalPromoted: risksToAdd.length,
              areaKey
            });
          }
        }
      } catch (scenError) {
        console.warn('[ExplorerDiscoveryService] RAG scenario generation failed:', scenError);
      }

      // Update area status to completed
      this.dispatch(updateExplorerAreaStatus({
        assessmentUuid: this.assessmentUuid,
        areaKey,
        status: 'completed'
      }));

      return {
        areaCard,
        selectedSources,
        causesBySource,
        relevantCausesForRAG,
        scenarios,
        ragMetadata,
        areaKey,
        version: 'explorer_v1'
      };

    } catch (error) {
      console.error('[ExplorerDiscoveryService] Fatal error:', error);

      // Update area status to error
      this.dispatch(updateExplorerAreaStatus({
        assessmentUuid: this.assessmentUuid,
        areaKey,
        status: 'error'
      }));

      throw error;
    }
  }

  async retryArea(areaKey, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg, inheritedRisks) {
    console.log(`[ExplorerDiscoveryService] Retrying area: ${areaKey}`);
    return this.executeDiscovery(areaKey, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg, inheritedRisks, null);
  }

  // Generate fallback causes when root cause analysis fails/times out
  generateFallbackCauses(selectedSources, areaDefinition) {
    const causesBySource = {};

    try {
      const riskSources = Array.isArray(areaDefinition?.riskSources) ? areaDefinition.riskSources : [];

      selectedSources.forEach(selectedSource => {
        const sourceDef = riskSources.find(rs => rs.name === selectedSource.sourceName);
        if (sourceDef?.causes) {
          const causes = [];
          Object.entries(sourceDef.causes).forEach(([category, categoryData]) => {
            const options = Array.isArray(categoryData.options) ? categoryData.options : [];
            options.slice(0, 3).forEach(option => {
              causes.push({
                name: option,
                evidence: `Potential organizational cause identified for ${selectedSource.sourceName}`,
                confidence: 0.7,
                category: category
              });
            });
          });

          if (causes.length > 0) {
            causesBySource[selectedSource.sourceName] = causes;
          }
        }
      });

      console.log(`[ExplorerDiscoveryService] Generated fallback causes for ${Object.keys(causesBySource).length} sources`);
    } catch (error) {
      console.error('[ExplorerDiscoveryService] Error generating fallback causes:', error);
    }

    return causesBySource;
  }

  // Extract relevant causes for RAG enhancement
  extractRelevantCausesForRAG(selectedSources, causesBySource, useCaseContext) {
    const relevantCauses = [];

    try {
      selectedSources.forEach(source => {
        const sourceCauseData = causesBySource[source.sourceName];

        if (Array.isArray(sourceCauseData)) {
          sourceCauseData.forEach(categoryResult => {
            if (categoryResult.researchedCauses && categoryResult.researchedCauses.length > 0) {
              categoryResult.researchedCauses.forEach(cause => {
                if (cause.confidence >= 0.4) {
                  const evaluationContext = Object.keys(cause)
                    .filter(key => !['name', 'description', 'evidence', 'confidence'].includes(key))
                    .map(key => `${key}: ${cause[key]}`)
                    .join('/');

                  relevantCauses.push({
                    source: source.sourceName,
                    sourceConfidence: source.aiConfidence,
                    causeName: cause.name,
                    description: cause.description,
                    evidence: cause.evidence,
                    confidence: cause.confidence,
                    category: categoryResult.category,
                    searchContext: `${source.sourceName} ${categoryResult.category}: ${cause.name}${evaluationContext ? ` (${evaluationContext})` : ''} - ${cause.evidence}`
                  });
                }
              });
            }
          });
        }
      });

      // Sort by combined confidence
      relevantCauses.sort((a, b) =>
        (b.sourceConfidence * b.confidence) - (a.sourceConfidence * a.confidence)
      );

      // Limit to top 10 for RAG
      return relevantCauses.slice(0, 10);
    } catch (error) {
      console.error('[ExplorerDiscoveryService] Error extracting relevant causes for RAG:', error);
      return [];
    }
  }

  // Save risks to database (parallel batch)
  async saveRisksToDatabase(risksToAdd, areaKey) {
    try {
      const savePromises = risksToAdd.map(riskItem => {
        const { category, risk } = riskItem;
        return authFetch('/.netlify/functions/saveRisk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            riskId: risk.riskId,
            name: risk.riskName,
            section: areaKey,
            category: category,
            type: 'Explorer',
            rag_source_file: null,
            modelUuid: this.modelId,
            assessmentUuid: this.assessmentUuid,
            owner: {
              name: this.user?.name || 'Unknown User',
              email: this.user?.email || 'unknown@email.com',
              picture: this.user?.picture || null
            }
          })
        });
      });

      const responses = await Promise.all(savePromises);
      const successCount = responses.filter(r => r.ok).length;
      console.log(`[ExplorerDiscoveryService] Saved ${successCount}/${risksToAdd.length} risks to database for area ${areaKey}`);

      if (successCount < risksToAdd.length) {
        console.warn(`[ExplorerDiscoveryService] Failed to save ${risksToAdd.length - successCount} risks to database`);
      }
    } catch (error) {
      console.error('[ExplorerDiscoveryService] Error saving risks to database:', error);
    }
  }

  // Validate area key is a valid Explorer area
  static isValidExplorerArea(areaKey) {
    return EXPLORER_RISK_AREAS.includes(areaKey);
  }

  // Get all Explorer risk areas
  static getExplorerAreas() {
    return EXPLORER_RISK_AREAS;
  }
}
